/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { Dropdown, DropdownMenuItem } from '@/components/UI/Dropdown';
import type { TanStackColumnDef, FilterOption, FilterOperation } from '@/components/UI/Table/TableComponent.model';
import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { errorToast, fetchMetadataColumns } from '@/helpers';
import { _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';
import InVivoError from '@/model/InVivoError.ts';
import type { MetadataField } from '@/model/Metadata.model';
import type { PresetCalculation } from '@/model/PresetCalculation.model';
import type { Study, StudyApiId } from '@/model/Study.model';
import type { StudyForm } from '@/model/StudyForm.model';
import { ApiService } from '@/support/ApiService';
import { web as webRoutes } from '@/support/route';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import _uniqBy from 'lodash/uniqBy';
import { RiMoreFill } from 'react-icons/ri';
import { notContainsOperation, containsOperation, selectOperations } from '@/utils/FiltersOperations.utils.ts';
import { NavigateFunction } from 'react-router-dom';

interface Types {
  calculations: Array<PresetCalculation>;
  title: string;
  id: ID;
}

export const generateStudiesMetadataColumns = async (studyMetadataFilters: Array<MetadataField> = []) => {
  return studyMetadataFilters.reduce<Array<MetadataField>>((activeMeta, metadataItem) => {
    if (metadataItem?.in_use && metadataItem.active) {
      activeMeta.push({ ...metadataItem, header: metadataItem.title });
    }
    return activeMeta;
  }, []);
};

export const metadataTypeIgnoredFilters = ['lookup_select', 'lookup_multi_select'];

export const studiesfilterOptions = (types: Array<Types>, metadata: Array<MetadataField>): Array<FilterOption> => {
  const uniqueTypes = _uniqBy(types, 'title').map((t) => ({
    id: t.id,
    name: t.title,
  }));

  const filters: Array<FilterOption> = [
    {
      value: 'type',
      name: 'Type',
      operations: [
        {
          name: filterType.eq,
          value: filterType.eq,
          options: uniqueTypes,
        },
        {
          name: filterType.ne,
          value: filterType.ne,
          options: uniqueTypes,
        },
      ],
    },
    {
      value: 'user',
      name: 'User',
      operations: [
        {
          name: 'is equal to',
          value: filterType.eq,
          options: [],
        },
      ],
      fetchFromApi: {
        placeholder: 'Search by user name',
        url: 'users.list',
      },
    },
  ];

  metadata?.forEach(({ field_type, id, title, options: fieldOptions }) => {
    if (!metadataTypeIgnoredFilters.includes(field_type)) {
      let operations: FilterOperation[];
      let fetchFromApi = undefined;
      switch (field_type) {
        case 'date':
          operations = [
            {
              name: filterType.contains,
              value: filterType.eq_date,
              options: [],
            },
            {
              name: filterType.not_contains,
              value: filterType.not_eq_date,
              options: [],
            },
          ];
          break;
        case 'multi_select': {
          operations = selectOperations;
          fetchFromApi = {
            placeholder: 'Multiselect options',
            url: 'GET /api/v1/metadata-glossary/{metadataGlossaryId}/used-options',
            params: { metadataGlossaryId: id },
            fieldType: 'multi_select',
            useModernApi: true,
          };
          break;
        }
        case 'select': {
          operations = selectOperations;
          fetchFromApi = {
            placeholder: 'Select options',
            url: 'GET /api/v1/metadata-glossary/{metadataGlossaryId}/used-options',
            params: { metadataGlossaryId: id },
            fieldType: 'select',
            useModernApi: true,
          };
          break;
        }
        default:
          operations = [...containsOperation, ...notContainsOperation];
      }

      filters.push({
        value: id,
        name: title,
        operations: operations,
        fetchFromApi: fetchFromApi,
      });
    }
  });

  return filters;
};

interface ActionColumnProps {
  navigate: NavigateFunction;
  studyForms?: Array<StudyForm>;
}

const cloneStudyCall = async (navigate: NavigateFunction, studyId: StudyApiId) => {
  try {
    const response = await ApiService.call({
      endpoint: 'POST /api/v1/studies/{studyId}/clone',
      path: { studyId },
    });

    if (response.type === 'success') {
      const { draftId, templateId: formId } = response.body;
      navigate(webRoutes('studies.new.draft', { formId, draftId }));
    }
  } catch (error) {
    errorToast('Could not clone study');
    ExceptionHandler.captureException(
      new InVivoError('Could not clone study', {
        cause: error,
        slug: 'dashboard-clone-study',
      })
    );
  }
};

export const buildActionColumn = ({ navigate, studyForms }: ActionColumnProps): Array<TanStackColumnDef<Study>> => {
  return [
    {
      id: 'actions',
      enableSorting: false,
      isVisible: true,
      meta: {
        lockVisibility: true,
        className: 'ui__table--overflow-cell',
      },
      cell: ({ row: { original: study } }: { row: { original: Study } }) => {
        return studyForms?.some((form) => form.id === study.study_creation_template_id) ? (
          <span className="cellActions" style={{ textAlign: 'right' }}>
            <Dropdown
              renderMenu={() => (
                <>
                  <DropdownMenuItem onClick={() => cloneStudyCall(navigate, study.api_id)}>
                    Clone Study
                  </DropdownMenuItem>
                </>
              )}
            >
              <RiMoreFill size={22} />
            </Dropdown>
          </span>
        ) : null;
      },
    },
  ];
};

export const generateColumns = (
  columns: Array<TanStackColumnDef<Study>>,
  metadata: Array<{ id: ID; header: string; title?: string; name?: string }>,
  navigate: NavigateFunction,
  studyForms?: Array<StudyForm>
): Array<TanStackColumnDef<Study>> => [
  ...columns,
  ...fetchMetadataColumns({ metadata }),
  ...buildActionColumn({ navigate, studyForms }),
];
